import { create } from 'zustand';

const useQuestionnaireStore = create((set, get) => ({
  // Questions from API
  questions: [],
  
  // Filtered questions (excluding skipped conditional questions)
  visibleQuestions: [],
  
  // Current step (0 = first question)
  currentStep: 0,
  
  // User answers: { questionId: { optionId, score } }
  answers: {},
  
  // Loading states
  isLoading: false,
  isSubmitting: false,
  
  // Result after submission
  result: null,
  
  // Error state
  error: null,

  // Calculate visible questions based on conditional logic
  updateVisibleQuestions: () => {
    const { questions, answers } = get();
    
    const visible = questions.filter(q => {
      // If no conditional, always show
      if (!q.conditional) return true;
      
      // Check if the dependent question is answered with one of the show_when values
      const dependsOn = q.conditional.depends_on;
      const showWhen = q.conditional.show_when || [];
      
      const dependentAnswer = answers[dependsOn];
      if (!dependentAnswer) return false;
      
      return showWhen.includes(dependentAnswer.optionId);
    });
    
    set({ visibleQuestions: visible });
  },

  // Actions
  setQuestions: (questions) => {
    set({ questions });
    // Initialize visible questions (all non-conditional)
    const visible = questions.filter(q => !q.conditional);
    set({ visibleQuestions: visible });
  },
  
  setCurrentStep: (step) => set({ currentStep: step }),
  
  nextStep: () => {
    const { currentStep, visibleQuestions } = get();
    if (currentStep < visibleQuestions.length - 1) {
      set({ currentStep: currentStep + 1 });
    }
  },
  
  prevStep: () => {
    const { currentStep } = get();
    if (currentStep > 0) {
      set({ currentStep: currentStep - 1 });
    }
  },
  
  setAnswer: (questionId, optionId, score) => {
    set((state) => ({
      answers: {
        ...state.answers,
        [questionId]: { optionId, score }
      }
    }));
    // Recalculate visible questions after answer
    get().updateVisibleQuestions();
  },
  
  setLoading: (isLoading) => set({ isLoading }),
  
  setSubmitting: (isSubmitting) => set({ isSubmitting }),
  
  setResult: (result) => set({ result }),
  
  setError: (error) => set({ error }),
  
  // Check if current question is answered
  isCurrentQuestionAnswered: () => {
    const { currentStep, visibleQuestions, answers } = get();
    const currentQuestion = visibleQuestions[currentStep];
    return currentQuestion && answers[currentQuestion.id] !== undefined;
  },
  
  // Get progress percentage
  getProgress: () => {
    const { currentStep, visibleQuestions } = get();
    if (visibleQuestions.length === 0) return 0;
    return Math.round(((currentStep + 1) / visibleQuestions.length) * 100);
  },
  
  // Check if this is the last question
  isLastQuestion: () => {
    const { currentStep, visibleQuestions } = get();
    return currentStep === visibleQuestions.length - 1;
  },
  
  // Reset questionnaire
  reset: () => set({
    currentStep: 0,
    answers: {},
    visibleQuestions: [],
    result: null,
    error: null,
    isSubmitting: false
  })
}));

export default useQuestionnaireStore;
